import CachedRoundedIcon from "@mui/icons-material/CachedRounded";
import { Button, Stack, Typography } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import InlineLoader from "../../../../../shared/components/inlineLoader/InlineLoader";
import biClient from "../../../../api/biApi";
import { useLocalization } from "../../../../hooks/useLocalization";
import DataRefreshIcon from "../../../../icons/DataRefreshIcon";
import {
  selectDrillDownCacheValid,
  selectEtag,
  selectSessionId,
} from "../../../../store/selectors/currentReportSelectors";
import { DrillDownPage } from "../../../../store/types/drillDownPageState.types";
import { createCurrentReportAction, createDrillDownPageAction } from "../../../../store/utils/storeUtils.ts";

interface Props {
  configuration: DrillDownPage;
}
export default function Loader({ configuration }: Props) {
  const { drilldown: locale } = useLocalization();
  const dispatch = useDispatch();

  const sessionId = useSelector(selectSessionId);
  const isCacheValid = useSelector(selectDrillDownCacheValid);
  const [error, setError] = useState<boolean>(false);

  const validationFailed = useDrillDownCacheValidation();

  useEffect(() => {
    if (configuration.info || !configuration.cell.drillDownId || !isCacheValid) return;
    let cancelled = false;
    biClient
      .getDrillDownItem(sessionId, configuration.cell.drillDownId)
      .then((data) => {
        if (cancelled) return;
        if (data?.success === true && data.data.item && data.data.notFound === false) {
          dispatch(createDrillDownPageAction("setInfo", { id: configuration.id, info: data.data.item }));
        } else {
          setError(true);
        }
      })
      .catch(() => {
        if (cancelled) return;
        setError(true);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configuration.cell.drillDownId, configuration.id, configuration.info, isCacheValid, sessionId]);

  return (
    <>
      {!error && !validationFailed && <InlineLoader text={locale.loading_drilldown_info} overlay={true} />}
      <Stack sx={{ px: 3, pb: 3, flex: 1, alignItems: "center", justifyContent: "center", gap: 1 }}>
        {(error || validationFailed) && <ErrorComponent />}
      </Stack>
    </>
  );
}

function ErrorComponent() {
  const { drilldown: locale } = useLocalization();
  const dispatch = useDispatch();

  const removeAllDrillDowns = useCallback(() => {
    dispatch(createDrillDownPageAction("removeAll"));
    dispatch(createCurrentReportAction("refresh"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Stack direction={"column"} sx={{ alignItems: "center", gap: 1, textAlign: "center" }}>
      <DataRefreshIcon />
      <Typography variant="h6">{locale.drilldown_info.error_caption}</Typography>
      <Typography color={"secondary"}>{locale.drilldown_info.error_text}</Typography>
      <Button
        variant="contained"
        color="primary"
        sx={{ mt: 2 }}
        startIcon={<CachedRoundedIcon />}
        onClick={removeAllDrillDowns}
      >
        Reload Report
      </Button>
    </Stack>
  );
}

function useDrillDownCacheValidation() {
  const dispatch = useDispatch();
  const sessionId = useSelector(selectSessionId);
  const etag = useSelector(selectEtag);
  const isCacheValid = useSelector(selectDrillDownCacheValid);
  const [error, setError] = useState<boolean>(false);

  const validateCache = useCallback(
    (isCancelled: () => boolean) => {
      if (etag === undefined || isCancelled() === true) return;
      biClient
        .isDrillDownCacheValid(sessionId, etag)
        .then((data) => {
          if (isCancelled()) return;
          if (data.success && !data.data.expired) {
            if (data.data.isCacheValid) {
              dispatch(createCurrentReportAction("setEtag", { etag, cacheValid: true }));
            } else {
              setTimeout(() => validateCache(isCancelled), 1500);
            }
          } else {
            dispatch(createCurrentReportAction("setEtag", { etag, cacheValid: false }));
            setError(true);
          }
        })
        .catch(() => {
          if (isCancelled()) return;
          dispatch(createCurrentReportAction("setEtag", { etag, cacheValid: false }));
          setError(true);
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [etag, sessionId]
  );

  useEffect(() => {
    if (isCacheValid && etag !== undefined) return;
    let cancelled = false;
    validateCache(() => cancelled);
    return () => {
      cancelled = true;
    };
  }, [etag, isCacheValid, sessionId, validateCache]);

  return error;
}
